'use client';
import { useState, useEffect } from 'react';
import { profileSchema, formatDocument, formatPhone, formatZipCode } from '../schemas/profile.schema';
import type { ProfileFormData } from '../schemas/profile.schema';
import type { ProfileData } from '../types/profile.types';

interface UseProfileFormProps {
	initialData: ProfileData | undefined;
}

interface FormErrors {
	[key: string]: string;
}

export const useProfileForm = ({ initialData }: UseProfileFormProps) => {
	const [formData, setFormData] = useState<ProfileFormData>({
		name: '',
		surName: '',
		email: '',
		phoneNumber: '',
		document: '',
		address: {
			street: '',
			district: '',
			city: '',
			state: '',
			zipCode: '',
			number: '',
		},
	});

	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

	useEffect(() => {
		if (initialData?.user) {
			setFormData({
				name: initialData.user.name || '',
				surName: initialData.user.surName || '',
				email: initialData.user.email || '',
				phoneNumber: initialData.user.phoneNumber || '',
				document: initialData.user.document || '',
				address: {
					street: initialData.user.address?.street || '',
					district: initialData.user.address?.district || '',
					city: initialData.user.address?.city || '',
					state: initialData.user.address?.state || '',
					zipCode: initialData.user.address?.zipCode || '',
					number: initialData.user.address?.number || '',
				},
			});
		}
	}, [initialData]);

	const handleChange = (field: string, value: string) => {
		let formattedValue = value;

		if (field === 'document') {
			formattedValue = formatDocument(value);
		} else if (field === 'phoneNumber') {
			formattedValue = formatPhone(value);
		} else if (field === 'address.zipCode') {
			formattedValue = formatZipCode(value);
		}

		let newFormData: ProfileFormData;

		if (field.startsWith('address.')) {
			const addressField = field.split('.')[1];
			newFormData = {
				...formData,
				address: {
					...formData.address,
					[addressField]: formattedValue,
				},
			};
		} else {
			newFormData = {
				...formData,
				[field]: formattedValue,
			};
		}

		setFormData(newFormData);

		if (touched[field]) {
			validateField(field, newFormData);
		}
	};

	const validateField = (field: string, data: ProfileFormData) => {
		const result = profileSchema.safeParse(data);

		if (result.success) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		} else {
			const fieldError = result.error.issues.find((err: any) => {
				const errorPath = err.path.join('.');
				return errorPath === field;
			});

			if (fieldError) {
				setErrors((prev) => ({
					...prev,
					[field]: fieldError.message,
				}));
			} else {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		}
	};

	const handleBlur = (field: string) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		validateField(field, formData);
	};

	const validateAll = (): boolean => {
		const fieldsToValidate = [
			'name',
			'surName',
			'email',
			'document',
			'phoneNumber',
			'address.zipCode',
			'address.street',
			'address.city',
			'address.number',
		];

		const allTouched: { [key: string]: boolean } = {};
		fieldsToValidate.forEach((field) => {
			allTouched[field] = true;
		});
		setTouched(allTouched);

		const result = profileSchema.safeParse(formData);

		if (result.success) {
			setErrors({});
			return true;
		} else {
			const newErrors: FormErrors = {};
			result.error.issues.forEach((err: any) => {
				const path = err.path.join('.');
				newErrors[path] = err.message;
			});
			setErrors(newErrors);
			return false;
		}
	};

	return {
		formData,
		errors,
		touched,
		handleChange,
		handleBlur,
		validateAll,
	};
};
